/**
 * useIsMobile — single source of truth for "are we on a phone-width viewport".
 *
 * Usage:
 *   const isMobile = useIsMobile()              // default: max-width 767px (Tailwind < md)
 *   const isNarrow = useIsMobile('(max-width: 639px)')  // custom query
 *
 * SSR-safe (returns false when `window`/`matchMedia` is unavailable) and
 * re-renders on breakpoint crossings. Replaces ad-hoc inline
 * `window.matchMedia(...)` calls (e.g. the sidebar collapse logic) so the
 * mobile breakpoint is defined in exactly one place.
 */

import { useState, useEffect } from 'react'

/** Tailwind `md` breakpoint is 768px, so "mobile" is everything below it. */
export const MOBILE_MEDIA_QUERY = '(max-width: 767px)'

export function useIsMobile(query: string = MOBILE_MEDIA_QUERY): boolean {
  const [matches, setMatches] = useState<boolean>(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false,
  )

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mq = window.matchMedia(query)
    const onChange = () => setMatches(mq.matches)
    onChange() // sync immediately in case the query changed between renders
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [query])

  return matches
}
